import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { api } from '@/api/client'
import type { QueueItem, RunStatus } from '@/api/types'
import {
  Badge,
  EmptyState,
  ErrorBox,
  PageHeader,
  Pagination,
  RunStatusBadge,
  Segmented,
  SkeletonRows,
} from '@/components/ui/Primitives'
import { ATS_LABEL, formatDateTime, formatDuration, formatNumber, hostOf, relativeTime } from '@/lib/utils'
import { useUrlState } from '@/lib/useUrlState'

const PAGE_SIZE = 50

export function SchedulerPage() {
  const queue = useQuery({ queryKey: ['queue'], queryFn: api.queue, refetchInterval: 4000 })
  const [filters, setFilters] = useUrlState({ show: '', page: '1' })
  const runsQuery = {
    status: (filters.show === 'failed' ? 'failed' : '') as RunStatus | '',
    new_only: filters.show === 'new',
    page: Number(filters.page) || 1,
    page_size: PAGE_SIZE,
  }
  const runs = useQuery({
    queryKey: ['runs', runsQuery],
    queryFn: () => api.runs(runsQuery),
    placeholderData: keepPreviousData,
    refetchInterval: 8000,
  })
  const q = queue.data

  return (
    <>
      <PageHeader
        title="Scheduler"
        subtitle="Every company's career pages are checked on their own interval; a check compares with the last one and records new and closed openings."
      />
      {queue.isError && <ErrorBox error={queue.error} onRetry={() => void queue.refetch()} />}

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Stat label="Checking now" value={q ? `${q.running.length} / ${q.concurrency}` : '—'} />
        <Stat label="Due now" value={formatNumber(q?.due_now)} />
        <Stat label="Due in 1 hour" value={formatNumber(q?.next_hour)} />
        <Stat label="Due in 24 hours" value={formatNumber(q?.next_24h)} />
        <Stat label="Scheduler off" value={formatNumber(q?.paused)} />
      </div>

      {q && (
        <div className="mb-6 flex flex-wrap items-center gap-x-5 gap-y-1.5 rounded-field border border-line bg-surface px-4 py-3 text-[13px] text-ink-soft">
          <b className="font-medium text-ink">Parallelism</b>
          <span>{q.concurrency} companies' career pages at once</span>
          <span>· {q.detail_concurrency} job pages at once per company</span>
          <span>· {q.per_host_concurrency} requests at once per site</span>
          <span>· {q.browser_concurrency} browser pages</span>
          <span>· up to {q.detail_max_per_crawl.toLocaleString()} descriptions per check</span>
          <span>· {q.it_jobs_only ? 'IT roles only' : 'all roles'}</span>
          <span className="text-ink-faint">— raise with CRAWLER_CONCURRENCY etc. on a bigger server, or run more workers</span>
        </div>
      )}

      <div className="mb-6 grid gap-6 lg:grid-cols-2">
        <QueueList title="Checking now" items={q?.running ?? []} empty="Nothing running — the scheduler is idle." running />
        <QueueList title="Up next" items={q?.upcoming ?? []} empty="Nothing scheduled." />
      </div>

      <section className="card overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
          <h2 className="text-[15px] font-semibold">Check history</h2>
          <Segmented
            size="sm"
            value={filters.show}
            onChange={(show) => setFilters({ show })}
            options={[
              { value: '', label: 'All checks' },
              { value: 'new', label: 'Found new openings' },
              { value: 'failed', label: 'Failed' },
            ]}
          />
        </div>
        <div className="overflow-x-auto">
          <table className="table-base min-w-[1000px]">
            <thead>
              <tr>
                <th>When</th>
                <th>Company</th>
                <th>Result</th>
                <th>Platform</th>
                <th className="text-right">Open</th>
                <th className="text-right">New</th>
                <th className="text-right">Closed</th>
                <th>Took</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody className={runs.isPlaceholderData ? 'opacity-60' : ''}>
              {runs.isPending && <SkeletonRows cols={9} />}
              {runs.data?.items.map((run) => (
                <tr key={run.id}>
                  <td className="whitespace-nowrap">
                    {formatDateTime(run.started_at)}
                    <span className="block text-[12px] text-ink-faint">{run.trigger === 'manual' ? 'manual' : 'scheduled'}</span>
                  </td>
                  <td className="max-w-[220px]">
                    <Link to={`/companies/${run.company_id}`} className="block truncate font-medium hover:underline">
                      {run.company_name}
                    </Link>
                    <span className="block truncate text-[12px] text-ink-muted">{hostOf(run.source_url)}</span>
                  </td>
                  <td>
                    <RunStatusBadge status={run.status} />
                  </td>
                  <td>{run.ats_type ? <Badge>{ATS_LABEL[run.ats_type]}</Badge> : '—'}</td>
                  <td className="text-right tabular-nums">{formatNumber(run.jobs_found)}</td>
                  <td className="text-right tabular-nums">
                    {run.jobs_new ? (
                      <Link
                        to={`/jobs?company_id=${run.company_id}&new_only=true&found_within_days=7&sort=recent`}
                        className="font-medium text-[#1f7a4d] hover:underline"
                      >
                        +{formatNumber(run.jobs_new)}
                      </Link>
                    ) : (
                      '0'
                    )}
                  </td>
                  <td className="text-right tabular-nums">{formatNumber(run.jobs_closed)}</td>
                  <td className="whitespace-nowrap">{formatDuration(run.duration_ms)}</td>
                  <td className="max-w-[340px] text-[12.5px]">
                    {run.error && <p className="line-clamp-2 text-danger" title={run.error}>{run.error}</p>}
                    {run.notes && <p className="line-clamp-2 text-ink-muted" title={run.notes}>{run.notes}</p>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {runs.data && runs.data.items.length === 0 && <EmptyState title="No checks match" />}
        {runs.data && runs.data.total > 0 && (
          <Pagination
            page={runs.data.page}
            pageSize={runs.data.page_size}
            total={runs.data.total}
            onPage={(p) => setFilters({ page: String(p) })}
          />
        )}
      </section>
    </>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="card px-4 py-3.5">
      <p className="label-eyebrow">{label}</p>
      <p className="mt-1.5 text-[22px] font-semibold tabular-nums">{value}</p>
    </div>
  )
}

function QueueList({ title, items, empty, running }: { title: string; items: QueueItem[]; empty: string; running?: boolean }) {
  return (
    <section className="card overflow-hidden">
      <h2 className="px-5 py-4 text-[15px] font-semibold">{title}</h2>
      {items.length === 0 ? (
        <p className="border-t border-line px-5 py-6 text-[13.5px] text-ink-muted">{empty}</p>
      ) : (
        <ul className="max-h-[420px] overflow-y-auto">
          {items.map((item) => (
            <li key={item.source_id} className="flex items-center gap-3 border-t border-line px-5 py-2.5 text-[13.5px]">
              {running && <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-success" />}
              <Link to={`/companies/${item.company_id}`} className="min-w-0 flex-1 truncate font-medium hover:underline">
                {item.company_name}
                <span className="ml-2 font-normal text-ink-muted">{hostOf(item.url)}</span>
              </Link>
              <Badge>{ATS_LABEL[item.ats_type]}</Badge>
              <span className="w-28 shrink-0 text-right text-[12.5px] text-ink-muted">
                {running
                  ? item.last_crawled_at ? 'checking' : 'first check'
                  : item.crawl_requested
                    ? 'requested now'
                    : new Date(item.next_crawl_at) <= new Date()
                      ? 'due now'
                      : relativeTime(item.next_crawl_at)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
